import { AttributesSelector, Button, Flex } from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { format } from 'date-fns'
import { ListingFunnelTimeRangeInputs } from './ListingFunnelTimeRangeInputs'
import { ListingFunnelTimeRangeFormSchema } from './listingFunnelTimeRangeFormSchema'

type Props = {
  defaultDateRange: { from: Date; to: Date }
  appliedDateRange: { from: Date; to: Date }
  maxEndDate: Date
  onApply: (dateRange: { from: Date; to: Date }) => void
}

const toFormValues = (dateRange: {
  from: Date
  to: Date
}): ListingFunnelTimeRangeFormSchema => ({
  timeRange: {
    dateRange: {
      from: dateRange.from,
      to: dateRange.to,
    },
  },
})

export const ListingFunnelTimeRangeFilter = ({
  defaultDateRange,
  appliedDateRange,
  maxEndDate,
  onApply,
}: Props) => {
  const methods = useForm<ListingFunnelTimeRangeFormSchema>({
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: toFormValues(defaultDateRange),
    values: toFormValues(appliedDateRange),
  })

  const selectedAttributes = useMemo(() => {
    const { from, to } = appliedDateRange
    return [`${format(from, 'MMM d, yyyy')} to ${format(to, 'MMM d, yyyy')}`]
  }, [appliedDateRange])

  const resetToDefaultValues = () => {
    methods.reset(toFormValues(defaultDateRange))
  }

  const handleApply = methods.handleSubmit(({ timeRange: { dateRange } }) => {
    const { from, to } = dateRange

    if (!from || !to) {
      return
    }

    onApply({ from, to })
  })

  return (
    <FormProvider {...methods}>
      <Flex alignItems="center" gap={2} flexWrap="wrap">
        <AttributesSelector
          label="Time Range"
          rootGroupTitle="Filter by"
          selectedAttributes={selectedAttributes}
          onClearAll={resetToDefaultValues}
          groups={
            <AttributesSelector.Group
              key="listing-funnel-time-range"
              title="Time range"
              numberOfSelectedAttributes={selectedAttributes.length}
            >
              <ListingFunnelTimeRangeInputs maxEndDate={maxEndDate} />
            </AttributesSelector.Group>
          }
        />
        <Button block={false} kind="medium" onClick={handleApply}>
          Search
        </Button>
      </Flex>
    </FormProvider>
  )
}
